(() => {
  /* =============================================
     HEADER & MOBILE MENU
     ============================================= */
  const header = document.querySelector('[data-header]');
  const menu = document.querySelector('[data-mobile-menu]');
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const menuClose = document.querySelector('[data-menu-close]');
  const menuBackdrop = document.querySelector('[data-menu-backdrop]');

  const closeMenu = () => {
    if (!menu || !menuToggle) return;
    menu.classList.remove('is-open');
    document.documentElement.classList.remove('menu-open');
    menuToggle.setAttribute('aria-expanded', 'false');
    setTimeout(() => { menu.hidden = true; }, 420);
  };

  const openMenu = () => {
    if (!menu || !menuToggle) return;
    menu.hidden = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        menu.classList.add('is-open');
        document.documentElement.classList.add('menu-open');
        menuToggle.setAttribute('aria-expanded', 'true');
      });
    });
  };

  menuToggle?.addEventListener('click', () => {
    menu?.classList.contains('is-open') ? closeMenu() : openMenu();
  });
  menuClose?.addEventListener('click', closeMenu);
  menuBackdrop?.addEventListener('click', closeMenu);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });

  /* =============================================
     HEADER HEIGHT SYNC
     ============================================= */
  const syncHeaderHeight = () => {
    if (!header) return;
    document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  };
  syncHeaderHeight();
  window.addEventListener('resize', syncHeaderHeight);

  /* =============================================
     PDP GALLERY
     ============================================= */
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const slider = gallery.querySelector('[data-gallery-slider]');
    const slides = gallery.querySelectorAll('[data-gallery-slide]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    const dots = gallery.querySelectorAll('[data-gallery-dot]');
    const isMobile = () => window.innerWidth <= 900;

    let currentIndex = 0;

    const goToSlide = (index) => {
      if (index < 0 || index >= slides.length) return;
      currentIndex = index;

      if (isMobile() && slider) {
        const slide = slides[index];
        slider.scrollTo({ left: slide.offsetLeft - slider.offsetLeft, behavior: 'smooth' });
      } else {
        slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      }

      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        if (i === index) t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      });

      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => goToSlide(parseInt(thumb.dataset.galleryThumb, 10)));
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => goToSlide(parseInt(dot.dataset.galleryDot, 10)));
    });

    if (slider) {
      let scrollTimer;
      slider.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (!isMobile()) return;
          const scrollLeft = slider.scrollLeft;
          const slideWidth = slides[0]?.offsetWidth || 1;
          const newIndex = Math.round(scrollLeft / slideWidth);
          if (newIndex !== currentIndex && newIndex >= 0 && newIndex < slides.length) {
            currentIndex = newIndex;
            dots.forEach((d, i) => d.classList.toggle('is-active', i === newIndex));
            thumbs.forEach((t, i) => t.classList.toggle('is-active', i === newIndex));
          }
        }, 50);
      }, { passive: true });
    }

    gallery.querySelectorAll('[data-zoom-wrap]').forEach((wrap) => {
      wrap.addEventListener('click', (e) => {
        if (isMobile()) return;
        const img = wrap.querySelector('img');
        if (!img) return;
        if (wrap.classList.contains('is-zoomed')) {
          wrap.classList.remove('is-zoomed');
          img.style.transformOrigin = '';
        } else {
          wrap.classList.add('is-zoomed');
          const rect = wrap.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`;
        }
      });
      wrap.addEventListener('mousemove', (e) => {
        if (!wrap.classList.contains('is-zoomed') || isMobile()) return;
        const rect = wrap.getBoundingClientRect();
        const img = wrap.querySelector('img');
        if (img) img.style.transformOrigin = `${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`;
      });
    });
  });

  /* =============================================
     VARIANT SELECTOR
     ============================================= */
  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const form = section.querySelector('[data-product-form]');
    const variantInput = section.querySelector('[data-variant-id]');
    const productJson = section.querySelector('[data-product-json]');
    const submit = section.querySelector('[data-add-to-cart]');
    const submitText = section.querySelector('[data-add-text]');
    const submitPrice = section.querySelector('[data-add-price]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const stickyBuy = document.querySelector('[data-sticky-buy]');
    const stickyAtc = document.querySelector('[data-sticky-atc]');
    const buyNow = section.querySelector('[data-buy-now]');
    const variants = productJson ? JSON.parse(productJson.textContent) : [];

    const formatMoney = (cents) => {
      const fmt = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'INR',
        minimumFractionDigits: 0,
      });
      return fmt.format(cents / 100);
    };

    const updateVariant = () => {
      if (!variants.length || !form || !variantInput) return;
      const selectedOptions = Array.from(form.querySelectorAll('[data-option-input]:checked')).map((i) => i.value);
      const variant = variants.find((v) => v.options.every((o, idx) => o === selectedOptions[idx]));
      if (!variant) return;

      variantInput.value = variant.id;
      if (submit) submit.disabled = !variant.available;
      if (buyNow) buyNow.disabled = !variant.available;
      if (submitText) submitText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      if (submitPrice) submitPrice.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (stickyBuy) {
        stickyBuy.disabled = !variant.available;
      }
      if (stickyAtc) {
        stickyAtc.disabled = !variant.available;
        stickyAtc.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }

      form.querySelectorAll('[data-option-input]').forEach((input) => {
        const pill = input.closest('.pdp-pill');
        if (pill) pill.classList.toggle('is-selected', input.checked);
      });

      form.querySelectorAll('[data-option-selected]').forEach((el) => {
        const name = el.dataset.optionSelected;
        const checked = form.querySelector(`[data-option-input][data-option-name="${name}"]:checked`);
        if (checked) el.textContent = checked.value;
      });
    };

    form?.addEventListener('change', updateVariant);

    buyNow?.addEventListener('click', () => {
      if (!form || !variantInput) return;
      const variantId = variantInput.value;
      window.location.href = `/cart/${variantId}:1`;
    });

    stickyBuy?.addEventListener('click', () => {
      if (!variantInput) return;
      window.location.href = `/cart/${variantInput.value}:1`;
    });

    stickyAtc?.addEventListener('click', () => {
      submit?.click();
    });
  });

  /* =============================================
     QUANTITY SELECTOR
     ============================================= */
  document.querySelectorAll('.pdp-qty, .quantity-selector').forEach((selector) => {
    const input = selector.querySelector('input');
    const minus = selector.querySelector('[data-quantity-minus]');
    const plus = selector.querySelector('[data-quantity-plus]');

    minus?.addEventListener('click', () => {
      input.value = Math.max(1, (Number(input.value) || 1) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    plus?.addEventListener('click', () => {
      input.value = (Number(input.value) || 1) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  /* =============================================
     STICKY MOBILE CTA
     ============================================= */
  const stickyCta = document.querySelector('[data-sticky-cta]');
  const pdpActions = document.querySelector('[data-pdp-actions]');

  if (stickyCta && pdpActions) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          stickyCta.classList.remove('is-visible');
        } else {
          stickyCta.classList.add('is-visible');
        }
      },
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    observer.observe(pdpActions);
  }

  /* =============================================
     REEL MODAL
     ============================================= */
  const reelModal = document.querySelector('[data-reel-modal]');
  const reelVideoContainer = document.querySelector('[data-reel-modal-video]');

  const openReelModal = (videoUrl) => {
    if (!reelModal) return;
    reelModal.hidden = false;
    document.documentElement.classList.add('menu-open');

    if (reelVideoContainer) {
      if (videoUrl && videoUrl.includes('youtube')) {
        const videoId = videoUrl.match(/(?:v=|youtu\.be\/)([^&?]+)/)?.[1];
        if (videoId) {
          reelVideoContainer.innerHTML = `<iframe src="https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0" allow="autoplay; encrypted-media" allowfullscreen></iframe>`;
        }
      } else if (videoUrl) {
        reelVideoContainer.innerHTML = `<video src="${videoUrl}" autoplay playsinline controls style="width:100%;height:100%;object-fit:cover;"></video>`;
      } else {
        reelVideoContainer.innerHTML = '<p style="color:rgba(255,255,255,.4);padding:20px;text-align:center;">Video preview</p>';
      }
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        reelModal.classList.add('is-open');
      });
    });
  };

  const closeReelModal = () => {
    if (!reelModal) return;
    reelModal.classList.remove('is-open');
    document.documentElement.classList.remove('menu-open');
    setTimeout(() => {
      reelModal.hidden = true;
      if (reelVideoContainer) reelVideoContainer.innerHTML = '';
    }, 320);
  };

  document.querySelectorAll('[data-reel-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      openReelModal(trigger.dataset.reelUrl || '');
    });
  });

  document.querySelectorAll('[data-reel-modal-close]').forEach((btn) => {
    btn.addEventListener('click', closeReelModal);
  });

  /* =============================================
     CART DRAWER
     ============================================= */
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartDrawerBackdrop = cartDrawer?.querySelector('[data-cart-drawer-backdrop]');
  const cartDrawerClose = cartDrawer?.querySelector('[data-cart-drawer-close]');

  const openCartDrawer = () => {
    if (!cartDrawer) return;
    cartDrawer.hidden = false;
    document.documentElement.classList.add('menu-open');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        cartDrawer.classList.add('is-open');
      });
    });
  };

  const closeCartDrawer = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    document.documentElement.classList.remove('menu-open');
    setTimeout(() => { cartDrawer.hidden = true; }, 420);
  };

  cartDrawerBackdrop?.addEventListener('click', closeCartDrawer);
  cartDrawerClose?.addEventListener('click', closeCartDrawer);

  document.querySelectorAll('[data-cart-drawer-toggle]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openCartDrawer();
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawer?.classList.contains('is-open')) closeCartDrawer();
    if (e.key === 'Escape' && reelModal?.classList.contains('is-open')) closeReelModal();
  });

  /* Intercept ATC form submit → open cart drawer */
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      if (!cartDrawer) return;
      e.preventDefault();

      const submitBtn = form.querySelector('[data-add-to-cart]');
      const submitText = form.querySelector('[data-add-text]');
      const originalText = submitText?.textContent;

      if (submitBtn) submitBtn.disabled = true;
      if (submitText) submitText.textContent = 'Adding...';

      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (res.ok) {
          if (submitText) submitText.textContent = 'Added ✓';
          await refreshCartDrawer();
          openCartDrawer();
          setTimeout(() => {
            if (submitText) submitText.textContent = originalText;
            if (submitBtn) submitBtn.disabled = false;
          }, 1500);
        } else {
          if (submitText) submitText.textContent = originalText;
          if (submitBtn) submitBtn.disabled = false;
        }
      } catch {
        if (submitText) submitText.textContent = originalText;
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  });

  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      renderCartDrawer(cart);
    } catch { /* silent */ }
  }

  function renderCartDrawer(cart) {
    const itemsContainer = cartDrawer.querySelector('[data-cart-drawer-items]');
    const emptyState = cartDrawer.querySelector('[data-cart-drawer-empty]');
    const footer = cartDrawer.querySelector('[data-cart-drawer-footer]');
    const subtotalEl = cartDrawer.querySelector('[data-cart-drawer-subtotal]');
    const countEl = cartDrawer.querySelector('[data-cart-drawer-count]');
    const headerCount = document.querySelector('.cart-count');

    if (countEl) countEl.textContent = cart.item_count;
    if (headerCount) headerCount.textContent = cart.item_count;

    if (!cart.items.length) {
      if (itemsContainer) itemsContainer.style.display = 'none';
      if (footer) footer.style.display = 'none';
      if (emptyState) emptyState.style.display = '';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (itemsContainer) itemsContainer.style.display = '';
    if (footer) footer.style.display = '';

    const formatPrice = (cents) => {
      const fmt = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cart.currency || 'INR',
        minimumFractionDigits: 0,
      });
      return fmt.format(cents / 100);
    };

    if (subtotalEl) subtotalEl.textContent = formatPrice(cart.total_price);

    if (itemsContainer) {
      itemsContainer.innerHTML = cart.items.map((item) => `
        <div class="cart-drawer__item">
          <div class="cart-drawer__item-img">
            ${item.image ? `<img src="${item.image.replace(/(\.[^.]+)$/, '_200x200$1')}" alt="${item.title}" loading="lazy">` : ''}
          </div>
          <div class="cart-drawer__item-info">
            <span class="cart-drawer__item-title">${item.product_title}</span>
            ${item.variant_title ? `<span class="cart-drawer__item-variant">${item.variant_title}</span>` : ''}
            <div class="cart-drawer__item-bottom">
              <div class="cart-drawer__item-qty">
                <button type="button" onclick="updateCartItem(${item.key}, ${item.quantity - 1})" aria-label="Decrease">
                  <svg class="icon" viewBox="0 0 24 24" fill="none"><path d="M5 12h14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
                </button>
                <span>${item.quantity}</span>
                <button type="button" onclick="updateCartItem(${item.key}, ${item.quantity + 1})" aria-label="Increase">
                  <svg class="icon" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
                </button>
              </div>
              <span class="cart-drawer__item-price">${formatPrice(item.final_line_price)}</span>
            </div>
            <button class="cart-drawer__item-remove" type="button" onclick="updateCartItem('${item.key}', 0)">Remove</button>
          </div>
        </div>
      `).join('');
    }
  }

  window.updateCartItem = async (key, qty) => {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(key), quantity: qty }),
      });
      await refreshCartDrawer();
    } catch { /* silent */ }
  };

  refreshCartDrawer();
})();
